import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { isEmpty } from "lodash";
import { useTranslation, withTranslation } from "react-i18next";
import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { request } from "../../../../utils/Request";
import { utcDateToLocalDate } from "../../../../utils/Helper";
import Auth from "../../../../utils/Auth";

const LOAD_COMMENT_MAX_LENGTH = 800;
const CURRENT_APP_ORIGIN = "alpc";

function buildLoadCommentKeysFromRow(row: any) {
    const typeMap: any = {
        AlpInboundLoad: "InboundLoad",
        AlpOutboundLoad: "OutboundLoad",
        AlpReexportLoad: "ReexportLoad",
        AlpSpotLoad: "SpotLoad",
        SpotLoad: "SpotLoad",
        InboundLoad: "InboundLoad",
        OutboundLoad: "OutboundLoad",
        ReexportLoad: "ReexportLoad",
    };

    const sourceType = typeMap[String(row?.type || row?.load_type || "").trim()]
        || String(row?.type || row?.load_type || "").trim();
    const sourceId = String(row?.load_source_id || row?.source_id || row?.id || "").trim();

    if ( !sourceType || !sourceId ) {
        return null;
    }

    return { sourceType, sourceId };
}

function isOutboundLoadGroup(row: any) {
    return String(row?.type || "").trim() === "AlpOutboundLoadGroup";
}

function getGroupToken(row: any) {
    return String(row?.load_source_id || row?.source_id || row?.id || row?.guid || "").trim();
}

function getOriginAppLabel(origin: any, t: any) {
    const slug = String(origin || "").trim().toLowerCase();

    if ( !slug || slug === CURRENT_APP_ORIGIN ) {
        return "";
    }

    if ( slug === "alpc" ) {
        return process.env.REACT_APP_LOAD_COMMENT_ORIGIN_ALPC || t("load comment origin alpc");
    }

    if ( slug === "acm" ) {
        return process.env.REACT_APP_LOAD_COMMENT_ORIGIN_ACM || t("load comment origin acm");
    }

    return slug.toUpperCase();
}

function getAuthorLabel(item: any, fallback: string) {
    return item?.author_name?.trim()
        || item?.author?.name?.trim()
        || item?.author?.email?.trim()
        || fallback;
}

function getAuthorInitials(label: string) {
    const parts = String(label || "").trim().split(/\s+/).filter(Boolean);

    if ( !parts.length ) {
        return "?";
    }

    if ( parts.length === 1 ) {
        return parts[0].slice(0, 2).toUpperCase();
    }

    return `${ parts[0][0] }${ parts[1][0] }`.toUpperCase();
}

function buildGroupLoadOptions(loads: any[]) {
    const options: any[] = [];
    const seen = new Set<string>();

    (loads || []).forEach((load: any) => {
        const sourceId = String(load?.source_id || load?.load_source_id || load?.id || "").trim();

        if ( !sourceId || seen.has(sourceId) ) {
            return;
        }

        seen.add(sourceId);

        const loadNumber = String(load?.load_number || sourceId).trim();
        options.push({
            label: loadNumber,
            value: sourceId,
        });
    });

    return options;
}

interface IFormProps {
    id: string | null,
    load: any | null,
    toastify?: any | null,
    onRefresh?: any | null,
    onClose?: any | null,
}

function LoadComments(props: IFormProps) {
    const { toastify } = props;
    const { t } = useTranslation();
    const auth = useMemo(() => new Auth(), []);
    const [entity, setEntity] = useState<any>(null);
    const [items, setItems] = useState<any[]>([]);
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [panelState, setPanelState] = useState<boolean>(false);
    const [groupLoadOptions, setGroupLoadOptions] = useState<any[]>([]);
    const [selectedGroupLoads, setSelectedGroupLoads] = useState<string[]>([]);
    const canAdd = auth.hasAnyAccess(["loadcommentsstore"]);

    const isGroupMode = isOutboundLoadGroup(entity);
    const sourceIdLabels = useMemo(() => {
        const map: any = {};

        groupLoadOptions.forEach((option: any) => {
            map[option.value] = option.label;
        });

        return map;
    }, [groupLoadOptions]);

    const onClose = () => {
        setPanelState(false);

        if ( props.onClose ) {
            props.onClose();
        }
    };

    const fetchGroupLoads = async (groupToken: string) => {
        const response = await request({
            url: `/suppliers/provider/aviso/outbound/groups/${ groupToken }`,
        });
        const data = response?.data?.data ?? response?.data;
        const loads = Array.isArray(data?.alp_outbound_loads) ? data.alp_outbound_loads : [];
        const options = buildGroupLoadOptions(loads);

        setGroupLoadOptions(options);
        setSelectedGroupLoads(options.map((option: any) => option.value));

        return options;
    };

    const getComments = async (load: any) => {
        setLoading(true);

        try {
            if ( isOutboundLoadGroup(load) ) {
                const groupToken = getGroupToken(load);

                if ( !groupToken ) {
                    toastify?.(t("action not allowed: 404"), "error");
                    return;
                }

                await fetchGroupLoads(groupToken);

                const response = await request({
                    url: "/suppliers/load/comments",
                    params: {
                        group_token: groupToken,
                        per_page: 200,
                    },
                });

                const { data } = response.data;
                setEntity(load);
                setItems(data ?? []);
                setMessage("");
                setPanelState(true);
                return;
            }

            const keys = buildLoadCommentKeysFromRow(load);

            if ( !keys ) {
                toastify?.(t("action not allowed: 404"), "error");
                return;
            }

            const response = await request({
                url: "/suppliers/load/comments",
                params: {
                    source_type: keys.sourceType,
                    source_id: keys.sourceId,
                    per_page: 200,
                },
            });

            const { data } = response.data;
            setEntity(load);
            setItems(data ?? []);
            setMessage("");
            setPanelState(true);
        } catch (error: any) {
            if ( error.status === 404 ) {
                toastify?.(t("action not allowed: 404"), "error");
            } else {
                toastify?.(t("server error"), "error");
            }
        } finally {
            setLoading(false);
        }
    };

    const onSubmit = async () => {
        const text = message.trim();

        if ( !entity || !text || saving ) {
            return;
        }

        if ( text.length > LOAD_COMMENT_MAX_LENGTH ) {
            toastify?.(t("load comment max length"), "error");
            return;
        }

        setSaving(true);

        try {
            if ( isGroupMode ) {
                const groupToken = getGroupToken(entity);

                if ( !groupToken || !selectedGroupLoads.length ) {
                    toastify?.(t("the field is required.", { field: t("load") }), "error");
                    setSaving(false);
                    return;
                }

                await request({
                    url: "/suppliers/load/comments",
                    method: "POST",
                    data: {
                        source_type: "OutboundLoadGroup",
                        source_id: groupToken,
                        target_source_ids: selectedGroupLoads,
                        comment: text,
                    },
                });
            } else {
                const keys = buildLoadCommentKeysFromRow(entity);

                if ( !keys ) {
                    return;
                }

                await request({
                    url: "/suppliers/load/comments",
                    method: "POST",
                    data: {
                        source_type: keys.sourceType,
                        source_id: keys.sourceId,
                        comment: text,
                    },
                });
            }

            toastify?.(t("saved successfully"), "info");
            setMessage("");
            await getComments(entity);

            if ( props.onRefresh ) {
                props.onRefresh();
            }
        } catch (error: any) {
            if ( error.status === 404 ) {
                toastify?.(t("action not allowed: 404"), "error");
            } else {
                toastify?.(t("server error"), "error");
            }
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        setEntity(null);
        setItems([]);
        setMessage("");
        setGroupLoadOptions([]);
        setSelectedGroupLoads([]);

        if ( !isEmpty(props.id) && !isEmpty(props.load) ) {
            // @ts-ignore
            getComments(props.load);
        }
    }, [props.id]);

    const messageLength = message.length;
    const isOverLimit = messageLength > LOAD_COMMENT_MAX_LENGTH;

    return (
        <>
            <div>
                {
                    entity &&
                  <>
                      <Sidebar
                        header={
                            <div className="bg-body-tertiary card-header">
                                <h5 className={ "card-title mt-0" }>
                                    { t('comments') }
                                    { entity?.load_number ? ` #${ entity.load_number }` : "" }
                                </h5>
                            </div>
                        }
                        visible={ panelState } position="right" onHide={ onClose } blockScroll={ true }
                        style={ { width: '26rem', maxWidth: '95vw' } } closeOnEscape={ false }
                      >
                          <div style={ {
                              height: '3px',
                              background: 'linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)'
                          } }></div>

                          <div className="load-comment-feed mt-4">
                              <div className="load-comment-feed__list">
                                  { loading && <div className="load-comment-feed__state">{ t("loading") }...</div> }
                                  { !loading && !items.length && <div className="load-comment-feed__state">{ t("no comments yet") }</div> }
                                  { !loading && items.map((item: any) => {
                                      const author = getAuthorLabel(item, t("system"));
                                      const originLabel = getOriginAppLabel(item.origin_app, t);
                                      const loadLabel = sourceIdLabels[item.source_id] || item.source_id;

                                      return (
                                          <div key={ item.id } className="load-comment-feed__message">
                                              <div className="load-comment-feed__avatar">{ getAuthorInitials(author) }</div>
                                              <div className="load-comment-feed__bubble">
                                                  <div className="load-comment-feed__meta">
                                                      <span className="load-comment-feed__author">{ author }</span>
                                                      { isGroupMode && loadLabel &&
                                                        <span className="load-comment-feed__origin">{ loadLabel }</span>
                                                      }
                                                      { originLabel &&
                                                        <span className="load-comment-feed__origin" title={ t("load comment from app", { app: originLabel }) }>
                                                            { originLabel }
                                                        </span>
                                                      }
                                                      <span className="load-comment-feed__time">
                                                          { item.created_at ? utcDateToLocalDate(item.created_at) : "-" }
                                                      </span>
                                                  </div>
                                                  <div className="load-comment-feed__text">{ item.comment }</div>
                                              </div>
                                          </div>
                                      );
                                  }) }
                              </div>

                              { canAdd &&
                                <div className="load-comment-feed__composer">
                                    { isGroupMode &&
                                      <MultiSelect
                                        value={ selectedGroupLoads }
                                        options={ groupLoadOptions }
                                        onChange={ (event) => setSelectedGroupLoads(event.value || []) }
                                        optionLabel="label"
                                        optionValue="value"
                                        placeholder={ t("load") }
                                        display="chip"
                                        className="w-100 mb-2"
                                        disabled={ saving || !groupLoadOptions.length }
                                      />
                                    }
                                    <div className="load-comment-feed__composer-row">
                                        <InputTextarea
                                          autoResize
                                          rows={ 2 }
                                          maxLength={ LOAD_COMMENT_MAX_LENGTH }
                                          value={ message }
                                          onChange={ (event) => setMessage(event.target.value) }
                                          disabled={ saving }
                                          placeholder={ `${ t("enter") } ${ t("comment").toLowerCase() }` }
                                          className="load-comment-feed__input w-100"
                                          onKeyDown={ (event) => {
                                              if ( event.key === "Enter" && !event.shiftKey ) {
                                                  event.preventDefault();
                                                  if ( message.trim() && !saving && !isOverLimit ) {
                                                      onSubmit();
                                                  }
                                              }
                                          } }
                                        />
                                        <Button
                                          label={ t("add") }
                                          icon="pi pi-send"
                                          size="small"
                                          className="load-comment-feed__send"
                                          disabled={ !message.trim() || isOverLimit || (isGroupMode && !selectedGroupLoads.length) }
                                          loading={ saving }
                                          onClick={ onSubmit }
                                        />
                                    </div>
                                    <span className={ ["load-comment-feed__counter", isOverLimit ? "is-over" : ""].filter(Boolean).join(" ") }>
                                        { messageLength } / { LOAD_COMMENT_MAX_LENGTH }
                                    </span>
                                </div>
                              }
                          </div>
                      </Sidebar>
                  </>
                }
            </div>
        </>
    );
}

LoadComments.propTypes = {
    id: PropTypes.any,
    load: PropTypes.any,
    toastify: PropTypes.any,
    onRefresh: PropTypes.any,
    onClose: PropTypes.any,
};

export default withTranslation()(LoadComments);
